import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConvexError } from "convex/values";
import { Doc } from "@cvx/_generated/dataModel";
import { MessageSquare } from "lucide-react";
import StatusBadge from "@/components/Generic/StatusBadge";
import { Textarea } from "@/components/ui/textarea";
import { VIDEO_EVENTS } from "@/constants/analyticsEvents";
import { VideoDiscussionThread } from "@/components/Generic/VideoDiscussionThread";
import { VideoEmbed } from "@/components/ui/video-embed";
import { api } from "@cvx/_generated/api";
import { format } from "date-fns";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { useMemo } from "react";

type Video = Doc<"contractVideos">;

export const ManualReviewDialog = ({
  video,
  onReviewCompleted,
  teamVideoReviewRequired = false,
}: {
  video: Video;
  onReviewCompleted?: () => void;
  teamVideoReviewRequired?: boolean;
}) => {
  const { toast } = useToast();
  const { track } = useMixpanel();
  const [isOpen, setIsOpen] = useState(false);
  const [decision, setDecision] = useState<"approved" | "rejected" | "">("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");

  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));

  const approveVideo = useConvexMutation(api.videoReview.approveContractVideo);
  const rejectVideo = useConvexMutation(api.videoReview.rejectContractVideo);
  const addDiscussion = useConvexMutation(api.videoReview.addVideoDiscussion);

  // Fetch all discussions for this video
  const { data: discussions, refetch: refetchDiscussions } = useQuery({
    ...convexQuery(api.videoReview.getVideoDiscussions, {
      contractVideoId: video._id,
    }),
    enabled: isOpen,
  });

  // Filter discussions based on user role and teamVideoReviewRequired
  const filteredDiscussions = useMemo(() => {
    if (!discussions || !user) return discussions;

    const isTeamMember = TEAM_USER_IDS.includes(user._id);

    // Team members always see everything
    if (isTeamMember) {
      return discussions;
    }

    // If teamVideoReviewRequired is false, brand accounts see everything
    if (!teamVideoReviewRequired) {
      return discussions;
    }

    // If teamVideoReviewRequired is true, brand accounts should only see:
    // 1. Their own comments
    // 2. Comments/replies posted AFTER their first initial comment

    // Find the brand user's first comment
    const brandUserFirstComment = discussions.find(
      (discussion) =>
        discussion.author === user._id && discussion.authorType === "brand",
    );

    // If they haven't commented yet, they see nothing
    if (!brandUserFirstComment) {
      return [];
    }

    const firstCommentTime = brandUserFirstComment._creationTime;

    // Filter: show user's own comments OR comments after their first comment
    return discussions.filter(
      (discussion) =>
        discussion.author === user._id ||
        discussion._creationTime > firstCommentTime,
    );
  }, [discussions, user, teamVideoReviewRequired]);

  // Determine the current review stage from video status
  const getReviewStage = (status: string): "ai" | "team" | "brand" => {
    if (
      status === "ai_reviewing" ||
      status === "ai_approved" ||
      status === "ai_rejected"
    )
      return "ai";
    if (
      status === "team_reviewing" ||
      status === "team_rejected" ||
      status === "needs_review"
    )
      return "team";
    return "brand";
  };

  // Auto-set decision to "rejected" for already approved videos since that's the only option
  useEffect(() => {
    if (video.status === "approved" && decision === "") {
      setDecision("rejected");
    }
  }, [video.status, decision]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!decision) {
      toast({
        title: "Decision required",
        description: "Please select approve or reject",
        variant: "destructive",
      });
      return;
    }

    if (decision === "rejected" && !message.trim()) {
      toast({
        title: "Rejection message required",
        description: "Please provide a message explaining your decision",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const stage = getReviewStage(video.status || "brand_reviewing");

      if (decision === "approved") {
        await approveVideo({
          videoId: video._id,
          stage,
          message: message.trim() || undefined,
        });

        // Track video approval
        track(VIDEO_EVENTS.VIDEO_APPROVED, {
          user_id: user?._id,
          video_id: video._id,
          contract_id: video.contractId,
          app_id: video.appId,
          creator_id: video.creatorId,
          approval_type: stage,
          previous_status: video.status,
          video_url: video.url,
          timestamp: new Date().toISOString(),
        });
      } else {
        await rejectVideo({
          videoId: video._id,
          stage,
          message: message.trim(),
        });

        // Track video rejection
        track(VIDEO_EVENTS.VIDEO_REJECTED, {
          user_id: user?._id,
          video_id: video._id,
          contract_id: video.contractId,
          app_id: video.appId,
          creator_id: video.creatorId,
          rejection_type: stage,
          previous_status: video.status,
          rejection_reason: message.trim(),
          video_url: video.url,
          timestamp: new Date().toISOString(),
        });
      }

      toast({
        title: `Video ${decision}`,
        description: `The video has been ${decision}`,
      });

      setIsOpen(false);
      setDecision("");
      setMessage("");
      refetchDiscussions();
      onReviewCompleted?.();
    } catch (error) {
      console.error("Error making review decision:", error);
      toast({
        title: "Error submitting decision",
        description:
          error instanceof ConvexError
            ? error.data
            : error instanceof Error
              ? error.message
              : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNewComment = async () => {
    if (!newCommentText.trim()) return;

    try {
      await addDiscussion({
        contractVideoId: video._id,
        message: newCommentText.trim(),
        action: "comment",
      });

      toast({
        title: "Comment added",
        description: "Your comment has been added",
      });

      setNewCommentText("");
      setShowAddComment(false);
      refetchDiscussions();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error adding comment",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error adding your comment",
        variant: "destructive",
      });
    }
  };

  const getButtonText = () => {
    const isReviewing = video.status?.includes("reviewing");
    if (isReviewing) return "Review";
    if (video.status === "approved") return "Override Approval";
    return "Review";
  };

  if (!video.status) return null;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          {getButtonText()}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[90vw] max-w-[1800px] sm:max-w-[1800px] p-0">
        <div className="h-full overflow-hidden">
          <div className="grid grid-cols-[1.2fr,0.8fr] gap-0 h-full">
            {/* Left Side - Video Preview (Full Height) */}
            <div className="bg-black flex items-center justify-center h-screen p-4">
              {video.url && (
                <VideoEmbed
                  url={video.url}
                  platform={video.platform}
                  className="w-full h-full max-h-[90vh]"
                />
              )}
            </div>

            {/* Right Side - Information, Comments, and Actions */}
            <div className="overflow-y-auto p-6 space-y-6 h-screen">
              {/* Header */}
              <div className="pb-4 border-b border-border">
                <h3 className="font-medium text-primary text-lg">
                  Video Review
                </h3>
                {video.title && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {video.title}
                  </p>
                )}
              </div>

              {/* Video Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-primary text-lg">
                  Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-primary/60">
                      Video Status
                    </label>
                    <div className="mt-1">
                      <StatusBadge status={video.status} />
                    </div>
                  </div>
                  {video.platform && (
                    <div>
                      <label className="text-sm font-medium text-primary/60">
                        Platform
                      </label>
                      <p className="mt-1 text-sm text-primary capitalize">
                        {video.platform}
                      </p>
                    </div>
                  )}
                </div>

                {video._creationTime && (
                  <div>
                    <label className="text-sm font-medium text-primary/60">
                      Submitted
                    </label>
                    <p className="mt-1 text-sm text-primary">
                      {format(
                        new Date(video._creationTime),
                        "MMM dd, yyyy 'at' HH:mm",
                      )}
                    </p>
                  </div>
                )}

                {video.postDate && (
                  <div>
                    <label className="text-sm font-medium text-primary/60">
                      Post Date
                    </label>
                    <p className="mt-1 text-sm text-primary">
                      {format(new Date(video.postDate), "MMM dd, yyyy")}
                    </p>
                  </div>
                )}

                {(video.views ||
                  video.likes ||
                  video.comments ||
                  video.shares) && (
                    <div>
                      <label className="text-sm font-medium text-primary/60">
                        Metrics
                      </label>
                      <div className="mt-1 flex gap-4 text-sm text-primary">
                        {video.views !== undefined && (
                          <span>👁️ {video.views.toLocaleString()} views</span>
                        )}
                        {video.likes !== undefined && (
                          <span>❤️ {video.likes.toLocaleString()}</span>
                        )}
                        {video.comments !== undefined && (
                          <span>💬 {video.comments.toLocaleString()}</span>
                        )}
                        {video.shares !== undefined && (
                          <span>🔁 {video.shares.toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  )}
              </div>

              {/* Discussion Thread */}
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    <h3 className="font-medium text-primary">
                      Discussion & Feedback
                    </h3>
                  </div>
                  {!showAddComment && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowAddComment(true)}
                    >
                      Add Comment
                    </Button>
                  )}
                </div>

                {/* Add Comment Form */}
                {showAddComment && (
                  <Card className="p-4 mb-4">
                    <div className="space-y-3">
                      <Textarea
                        placeholder="Write your comment..."
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                        className="min-h-[80px]"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleAddNewComment}
                          disabled={!newCommentText.trim()}
                        >
                          Add Comment
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowAddComment(false);
                            setNewCommentText("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Discussion Messages */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  <VideoDiscussionThread
                    discussions={filteredDiscussions}
                    emptyMessage="No discussion yet. Be the first to comment!"
                  />
                </div>
              </div>

              {/* Review Actions */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="font-medium text-primary">Review Actions</h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-primary">
                      Your Decision
                    </label>
                    <div className="flex gap-2 mt-2">
                      {video.status !== "approved" && (
                        <Button
                          type="button"
                          variant={
                            decision === "approved" ? "default" : "outline"
                          }
                          onClick={() => setDecision("approved")}
                          className="flex-1"
                        >
                          ✅ Approve
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant={
                          decision === "rejected" ? "destructive" : "outline"
                        }
                        onClick={() => setDecision("rejected")}
                        className={
                          video.status === "approved" ? "w-full" : "flex-1"
                        }
                      >
                        ❌ Reject
                      </Button>
                    </div>
                  </div>

                  {decision === "rejected" && (
                    <div>
                      <label
                        htmlFor="message"
                        className="text-sm font-medium text-primary"
                      >
                        Rejection Message
                      </label>
                      <Textarea
                        id="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Explain why this video doesn't meet the requirements..."
                        className="mt-1"
                        rows={4}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        This message will be added to the discussion thread so
                        the creator can respond.
                      </p>
                    </div>
                  )}

                  {decision === "approved" && (
                    <div>
                      <label
                        htmlFor="approvalMessage"
                        className="text-sm font-medium text-primary"
                      >
                        Approval Message (Optional)
                      </label>
                      <Textarea
                        id="approvalMessage"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Add any additional comments or feedback..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  )}

                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsOpen(false)}
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        !decision ||
                        (decision === "rejected" && !message.trim()) ||
                        isSubmitting
                      }
                      className="flex-1"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Decision"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
